<template>
  <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
    <!-- Compact Single Row Layout -->
    <div class="tw-flex items-center gap-3 flex-wrap">
      <!-- Name Search -->
      <div class="tw-flex items-center gap-2">
        <span class="actors-filter-label">{{ $t('knowledge_base_search_label') }}</span>
        <div class="tw-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-md transition-all duration-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10" style="min-width: 180px;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-gray-400 shrink-0">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            :value="search"
            @input="$emit('update:search', $event.target.value)"
            :placeholder="$t('actors_filter_search_placeholder')"
            class="border-none outline-none text-sm text-gray-700 bg-transparent placeholder-gray-400 w-full"
          />
          <button
            v-if="search"
            @click="$emit('update:search', '')"
            class="tw-flex items-center shrink-0 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-0"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Each Facet as a Dropdown -->
      <template v-for="facet in availableFacets" :key="getFacetKey(facet)">
        <div class="relative">
          <button
            @click="toggleFacetDropdown(getFacetKey(facet))"
            class="tw-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer transition-all duration-200 whitespace-nowrap hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
            :class="{
              'bg-primary-extra-lightest border-primary text-primary': getSelectedValuesForFacet(facet.name).length > 0
            }"
          >
            <span class="font-medium">{{ getFacetLabel(facet) }}</span>
            <span
              v-if="getSelectedValuesForFacet(facet.name).length > 0"
              class="tw-flex items-center justify-center min-w-5 h-5 px-1.5 bg-primary text-white rounded-full text-xs font-semibold"
            >
              {{ getSelectedValuesForFacet(facet.name).length }}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="ml-1 opacity-60">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          <!-- Facet Values Dropdown -->
          <div
            v-if="openFacetDropdown === getFacetKey(facet)"
            class="absolute top-full left-0 mt-2 min-w-[250px] max-w-[320px] bg-white border border-gray-300 rounded-lg shadow-lg z-50"
          >
            <!-- Search Input -->
            <div class="tw-flex items-center gap-2 px-3 py-2 border-b border-gray-200">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-gray-400 shrink-0">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                type="text"
                v-model="dropdownSearchQuery"
                :placeholder="$t('actors_filter_search_placeholder')"
                class="flex-1 border-none outline-none text-sm text-gray-700 bg-transparent placeholder-gray-400"
                @click.stop
              />
            </div>

            <div class="max-h-[280px] overflow-y-auto py-1">
              <!-- All Option -->
              <button
                v-if="!dropdownSearchQuery"
                @click="clearFacetSelection(facet.name, getFacetKey(facet))"
                class="block w-full px-3 py-2 text-sm text-gray-700 text-left bg-transparent border-none cursor-pointer transition-colors duration-150 hover:bg-gray-100"
                :class="{
                  'bg-primary-extra-lightest text-primary font-medium': getSelectedValuesForFacet(facet.name).length === 0
                }"
              >
                {{ $t('categories_all') }}
              </button>

              <!-- Separator -->
              <div v-if="!dropdownSearchQuery" class="h-px bg-gray-200 my-1"></div>

              <!-- Facet Value Options (checkboxes for multi-select) -->
              <label
                v-for="item in getFilteredFacetItems(facet)"
                :key="item.value"
                class="tw-flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 cursor-pointer transition-colors duration-150 hover:bg-gray-100"
              >
                <input
                  type="checkbox"
                  :checked="isValueSelected(facet.name, item.value)"
                  @change="toggleFacetValue(facet.name, item.value, item.label)"
                  class="w-4 h-4 accent-primary cursor-pointer"
                />
                <span class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{{ getItemLabel(facet, item) }}</span>
                <span class="text-gray-400 text-xs shrink-0">({{ item.count || 0 }})</span>
              </label>

              <!-- No results message -->
              <div v-if="getFilteredFacetItems(facet).length === 0" class="px-3 py-4 text-center text-gray-400 text-sm">
                {{ $t('no_results_found') }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- See More/Less Button -->
      <button
        v-if="hasMoreFacets"
        @click="showAllFacets = !showAllFacets"
        class="tw-flex items-center gap-1 px-3 py-2 text-sm text-primary font-medium cursor-pointer transition-all duration-200 hover:bg-gray-50 rounded-md"
      >
        <span v-if="!showAllFacets">+ {{ hiddenFacetsCount }} {{ $t('actors_more_filters') }}</span>
        <span v-else>{{ $t('actor_detail_show_less') }}</span>
      </button>

      <!-- Actions Group -->
      <div v-if="hasActiveFilters" class="ml-auto tw-flex items-center gap-2">
        <button
          @click="clearAllFilters"
          class="tw-flex items-center justify-center w-8 h-8 bg-gray-100 text-gray-500 border border-gray-200 rounded-md cursor-pointer transition-all duration-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
          :title="$t('actors_clear_all_filters')"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Active Filters Section -->
    <div v-if="hasActiveFilters" class="tw-flex items-center gap-3 pt-3 mt-3 border-t border-gray-100 flex-wrap">
      <div class="font-medium text-xs text-gray-500 whitespace-nowrap uppercase tracking-wide">
        {{ $t('challenges_active_filters') }}
      </div>
      <div class="tw-flex flex-wrap gap-2 flex-1">
        <!-- Active Search Tag -->
        <span
          v-if="search"
          class="tw-flex items-center gap-1.5 bg-primary text-white border border-primary rounded-full px-2.5 py-1 text-xs font-medium cursor-pointer transition-all duration-200 whitespace-nowrap hover:bg-primary-lighter hover:border-primary-lighter hover:text-primary hover:-translate-y-px"
          @click="$emit('update:search', '')"
        >
          {{ search }}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="opacity-80 hover:opacity-100">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </span>
        <!-- Active Facet Value Tags -->
        <span
          v-for="filter in activeFilterTags"
          :key="filter.facet + '-' + filter.value"
          class="tw-flex items-center gap-1.5 bg-primary text-white border border-primary rounded-full px-2.5 py-1 text-xs font-medium cursor-pointer transition-all duration-200 whitespace-nowrap hover:bg-primary-lighter hover:border-primary-lighter hover:text-primary hover:-translate-y-px"
          @click="removeFilter(filter)"
        >
          {{ filter.label }}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="opacity-80 hover:opacity-100">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import TranslationsMixin from '@/util/TranslationsMixin'

  export default defineComponent({
    name: 'ActorsFilters',
    mixins: [TranslationsMixin],
    props: {
      facetCounts: {
        type: Array,
        default: () => []
      },
      keywords: {
        type: Array,
        default: () => []
      },
      availableFilterControls: {
        type: Array,
        default: () => []
      },
      getTaxonomyAlias: {
        type: Function,
        default: (name) => name
      },
      search: {
        type: String,
        default: ''
      }
    },
    emits: ['facet-selected', 'facet-cleared', 'filter-removed', 'filters-cleared', 'update:search'],
    data () {
      return {
        openFacetDropdown: null,
        dropdownSearchQuery: '',
        showAllFacets: false,
        maxVisibleFacets: 7
      }
    },
    computed: {
      allAvailableFacets () {
        return this.facetCounts.filter(facet => {
          if (!facet.count || facet.count.length === 0) return false
          // Only show facets that are in availableFilterControls
          if (!this.availableFilterControls.includes(facet.name) && facet.name !== 'portfolio_ids') {
            // Also allow report fields
            if (!facet.name.startsWith('report_field_')) return false
          }
          return true
        })
      },
      availableFacets () {
        if (this.showAllFacets) {
          return this.allAvailableFacets
        }
        return this.allAvailableFacets.slice(0, this.maxVisibleFacets)
      },
      hasMoreFacets () {
        return this.allAvailableFacets.length > this.maxVisibleFacets
      },
      hiddenFacetsCount () {
        return this.allAvailableFacets.length - this.maxVisibleFacets
      },
      hasActiveFilters () {
        return this.activeFilterTags.length > 0 || !!this.search
      },
      activeFilterTags () {
        const tags = []

        // Get all active keywords that belong to available facets
        for (const keyword of this.keywords) {
          if (keyword.facet && keyword.value !== '-') {
            const facet = this.allAvailableFacets.find(f => f.name === keyword.facet)
            if (facet) {
              const facetItem = facet.count.find(item => item.value === keyword.value)
              tags.push({
                facet: keyword.facet,
                value: keyword.value,
                label: `${this.getFacetLabel(facet)}: ${facetItem ? this.getItemLabel(facet, facetItem) : keyword.value}`
              })
            }
          }
        }

        return tags
      }
    },
    mounted () {
      document.addEventListener('click', this.handleClickOutside)
    },
    beforeUnmount () {
      document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
      getItemLabel (facet, item) {
        const translated = this.getLabelForTaxonomyValue(facet.name, item.value, this.locale)
        return translated || item.label
      },
      getFacetKey (facet) {
        // Create a unique key using name and parent_id if available
        return facet.parent_id ? `${facet.name}-${facet.parent_id}` : facet.name
      },
      getFacetLabel (facet) {
        if (facet.name === 'portfolio_ids') {
          return 'Portfolio'
        }
        if (facet.name === 'city') {
          return this.$t('edit_announcement_city')
        }
        if (facet.name === 'sustainability_goal') {
          return this.$t('actor_detail_sdgs')
        }
        if (facet.parent_name) {
          return facet.parent_name
        }
        return this.getTaxonomyAlias(facet.name)
      },
      toggleFacetDropdown (facetName) {
        if (this.openFacetDropdown === facetName) {
          this.openFacetDropdown = null
        } else {
          this.openFacetDropdown = facetName
          this.dropdownSearchQuery = ''
        }
      },
      getFilteredFacetItems (facet) {
        let items = facet.count

        if (this.dropdownSearchQuery) {
          const query = this.dropdownSearchQuery.toLowerCase()
          items = items.filter(item =>
            item.label && item.label.toLowerCase().includes(query)
          )
        }

        // Sort items - for SDGs sort by numeric value, otherwise alphabetically
        return [...items].sort((a, b) => {
          if (facet.name === 'sustainability_goal') {
            // Extract numbers from SDG labels (e.g., "SDG 1" -> 1)
            const numA = parseInt(a.value) || parseInt(a.label?.match(/\d+/)?.[0]) || 0
            const numB = parseInt(b.value) || parseInt(b.label?.match(/\d+/)?.[0]) || 0
            return numA - numB
          }
          // Default: sort alphabetically by label
          return (a.label || '').localeCompare(b.label || '')
        })
      },
      getSelectedValuesForFacet (facetName) {
        return this.keywords.filter(k => k.facet === facetName).map(k => k.value)
      },
      isValueSelected (facetName, value) {
        return this.keywords.some(k => k.facet === facetName && k.value === value)
      },
      toggleFacetValue (facetName, value, label) {
        this.$emit('facet-selected', { facet: facetName, value, label })
      },
      clearFacetSelection (facetName, facetKey) {
        this.$emit('facet-cleared', facetName)
        if (this.openFacetDropdown === facetKey) {
          this.openFacetDropdown = null
        }
      },
      removeFilter (filter) {
        this.$emit('filter-removed', { facet: filter.facet, value: filter.value })
      },
      clearAllFilters () {
        this.$emit('filters-cleared')
        this.$emit('update:search', '')
      },
      handleClickOutside (event) {
        if (!this.$el.contains(event.target)) {
          this.openFacetDropdown = null
        }
      }
    }
  })
</script>

<style scoped lang="scss">
  .actors-filter-label {
    font-weight: 500;
    font-size: 0.75rem;
    color: #6b7280;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  /* Custom color variables that reference CSS custom properties */
  .bg-primary {
    background-color: var(--primary-community, #3b82f6);
  }

  .bg-primary-extra-lightest {
    background-color: var(--primary-community-extra-lightest, #eff6ff);
  }

  .bg-primary-lighter {
    background-color: var(--primary-community-lighter, #dbeafe);
  }

  .text-primary {
    color: var(--primary-community, #3b82f6);
  }

  .border-primary {
    border-color: var(--primary-community, #3b82f6);
  }

  .border-primary-lighter {
    border-color: var(--primary-community-lighter, #dbeafe);
  }

  .accent-primary {
    accent-color: var(--primary-community, #3b82f6);
  }

  .ring-primary\/10 {
    --tw-ring-color: rgba(var(--primary-community-rgb, 59, 130, 246), 0.1);
  }

  .hover\:bg-primary-lighter:hover {
    background-color: var(--primary-community-lighter, #dbeafe);
  }

  .hover\:border-primary-lighter:hover {
    border-color: var(--primary-community-lighter, #dbeafe);
  }

  .hover\:text-primary:hover {
    color: var(--primary-community, #3b82f6);
  }

  /* Responsive styles */
  @media (max-width: 768px) {
    .filter-dropdown {
      position: fixed !important;
      left: 0.5rem !important;
      right: 0.5rem !important;
      min-width: auto !important;
      max-width: none !important;
    }
  }
</style>
